import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Lock, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarDistancia, mensagemDeErro } from '@/lib/format';
import {
  NomeDePessoa,
  useDicionarioDePessoas,
  type DicionarioDePessoas,
} from '@/features/tickets/pessoas';
import { chaves, comentar, listarComentarios } from '@/features/tickets/tickets.api';
import type { Comentario, VisibilidadeDeComentario } from '@/features/tickets/types';

/**
 * Conversa do chamado.
 *
 * Público e interno precisam ser **distinguíveis à distância**: o custo de
 * confundir os dois é escrever para o cliente o que era para a equipe. Daí a
 * faixa lateral, o fundo próprio e o cadeado — três sinais, não só a cor.
 */
export function CommentsPanel({ ticketId }: { ticketId: string }) {
  const clienteDeConsulta = useQueryClient();

  const [texto, setTexto] = useState('');
  const [interno, setInterno] = useState(false);

  const comentarios = useQuery({
    queryKey: chaves.comentarios(ticketId),
    queryFn: () => listarComentarios(ticketId),
  });

  // Autores da conversa inteira numa resolução só; quem já apareceu no
  // cabeçalho do chamado sai do cache, sem nova ida à rede.
  const pessoas = useDicionarioDePessoas(
    comentarios.data?.map((comentario) => comentario.authorId) ?? [],
  );

  const publicar = useMutation<Comentario, Error, void>({
    mutationFn: () =>
      comentar(ticketId, {
        bodyHtml: textoParaHtml(texto),
        visibility: interno ? 'INTERNAL' : 'PUBLIC',
      }),
    onSuccess: () => {
      setTexto('');
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.comentarios(ticketId) });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!texto.trim()) return;
    publicar.mutate();
  }

  return (
    <div className="space-y-5">
      {comentarios.isPending ? (
        <p className="text-sm text-muted-foreground">Carregando conversa…</p>
      ) : comentarios.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDeErro(comentarios.error)}
        </p>
      ) : comentarios.data.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-5 py-10 text-center">
          <MessageSquare className="h-5 w-5 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            Nada foi registrado ainda. O primeiro comentário público é o que o solicitante recebe.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {comentarios.data.map((comentario) => (
            <ItemDeComentario key={comentario.id} comentario={comentario} pessoas={pessoas} />
          ))}
        </ul>
      )}

      <form onSubmit={enviar} className="space-y-3 rounded-md border border-border bg-card p-3">
        <textarea
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          rows={4}
          aria-label={interno ? 'Nota interna' : 'Resposta ao solicitante'}
          placeholder={
            interno ? 'Nota visível apenas para a equipe.' : 'Resposta visível para o solicitante.'
          }
          className={cn(
            'w-full resize-y rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm',
            'transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow',
            interno && 'border-warning/40 bg-warning/5',
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={interno}
              onChange={(evento) => setInterno(evento.target.checked)}
              className="h-4 w-4 rounded border-border accent-[hsl(var(--warning))]"
            />
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" aria-hidden />
              Nota interna
            </span>
          </label>

          <Button
            type="submit"
            size="sm"
            loading={publicar.isPending}
            disabled={!texto.trim()}
            icon={<Send className="h-4 w-4" aria-hidden />}
          >
            {interno ? 'Registrar nota' : 'Responder'}
          </Button>
        </div>

        {publicar.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {mensagemDeErro(publicar.error)}
          </p>
        ) : null}
      </form>
    </div>
  );
}

const ESTILO_POR_VISIBILIDADE: Record<VisibilidadeDeComentario, string> = {
  PUBLIC: 'border-l-primary bg-card',
  INTERNAL: 'border-l-warning bg-warning/5',
  SYSTEM: 'border-l-border bg-muted/40',
};

function ItemDeComentario({
  comentario,
  pessoas,
}: {
  comentario: Comentario;
  pessoas: DicionarioDePessoas;
}) {
  return (
    <li
      className={cn(
        'animate-fade-in rounded-md border border-border border-l-4 px-4 py-3',
        ESTILO_POR_VISIBILIDADE[comentario.visibility],
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="font-medium">
          {comentario.visibility === 'SYSTEM' ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Bot className="h-3.5 w-3.5" aria-hidden />
              Sistema
            </span>
          ) : (
            // `authorId` nulo é comentário gravado fora de uma sessão de
            // usuário — o servidor guarda `null` quando o ator não é pessoa.
            <NomeDePessoa id={comentario.authorId} pessoas={pessoas} semId="sem autor registrado" />
          )}
        </span>

        {comentario.visibility === 'INTERNAL' ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 font-semibold text-warning ring-1 ring-inset ring-warning/30">
            <Lock className="h-3 w-3" aria-hidden />
            Interno
          </span>
        ) : null}

        <span className="text-muted-foreground" title={formatarDataHora(comentario.createdAt)}>
          {formatarDistancia(comentario.createdAt)}
        </span>

        {comentario.editedAt ? (
          <span className="text-muted-foreground" title={formatarDataHora(comentario.editedAt)}>
            (editado)
          </span>
        ) : null}
      </div>

      {/*
        O HTML vem higienizado do servidor — `prepareRichText` roda em toda
        gravação de comentário, com lista de permissão. É por isso que aqui a
        marcação é injetada em vez de escapada: sem ela, negrito, lista e
        citação de e-mail chegariam ao operador como texto com tags à vista.
      */}
      <div
        className="text-sm leading-relaxed [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_code]:font-mono [&_code]:text-xs [&_li]:ml-4 [&_li]:list-disc [&_p]:mb-2 [&_p:last-child]:mb-0 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2"
        dangerouslySetInnerHTML={{ __html: comentario.bodyHtml }}
      />
    </li>
  );
}

/** Mesma conversão da abertura de chamado: texto digitado vira HTML escapado. */
function textoParaHtml(texto: string): string {
  const escapar = (trecho: string) =>
    trecho.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return texto
    .trim()
    .split(/\n{2,}/)
    .map((paragrafo) => `<p>${escapar(paragrafo).replace(/\n/g, '<br />')}</p>`)
    .join('');
}
